/**
 * Helpers injected into the DB Manager guest source (entry.js) with
 * Function.prototype.toString — the guest is a string evaluated in a sandboxed
 * iframe, so nothing can be imported there. Keep every function here
 * self-contained ES5: no imports, no module-scope references, no arrow
 * functions, spread or classes. Jest imports these same functions directly.
 */

/**
 * Mark every lazy branch under `node` as not loaded, KEEPING what it shows.
 *
 * Refresh used to reset only the top listing, and the database/schema nodes it
 * kept (on purpose — so open subtrees stay open) still said `loaded: true`
 * with their old table lists; a table created since never appeared until a
 * disconnect/reconnect (owner, 30.09.2026). After this, the next publish makes
 * the host re-request every EXPANDED lazy node (ExtensionTreeSection), and a
 * collapsed one reloads on its next expand. `resolvedChildren` stays, so the
 * tree does not flash empty while the queries run.
 */
export function invalidateLazy(node) {
  if (!node || typeof node !== 'object') return;
  var kids;
  if (node.children === 'lazy') {
    node.loaded = false;
    kids = node.resolvedChildren || [];
  } else {
    kids = Array.isArray(node.children) ? node.children : [];
  }
  for (var i = 0; i < kids.length; i++) invalidateLazy(kids[i]);
}

/**
 * Run `call()` (returning a promise); if it rejects, wait `delays[n]` ms and
 * try again, up to `delays.length` retries, then reject with the last error.
 *
 * For the first `db.listSavedConnections` on activation: one failed call was
 * read as "nothing saved" and left "No connections yet" for the session.
 * (The empty sidebar seen on 30.09.2026 turned out to be a lost READY/INIT
 * handshake — fixed in sdk/guestSdk.js — this retry is the defensive half.)
 */
export function retryCall(call, delays) {
  var attempt = 0;
  function run() {
    return call().catch(function (e) {
      if (attempt >= delays.length) throw e;
      var wait = delays[attempt];
      attempt += 1;
      return new Promise(function (resolve) { setTimeout(resolve, wait); }).then(run);
    });
  }
  return run();
}
